// Appearance: Dark (default) or Light, per device.
//
// Saved in this browser's localStorage only (no database): a phone and a tablet
// of the same user may differ. Off by default. `data-theme="light"` on <html>
// turns the light tokens on (src/index.css, docs/specs/DESIGN-SYSTEM.md §1).
//
// index.html's inline boot script applies the same rule BEFORE first paint, so
// keep the two in step (key, value, the /device exclusion). Shared-device kiosk
// screens under /device always stay dark: one person's choice must not change a
// screen the next worker walks up to.
import { useSyncExternalStore } from 'react'
import { SHARED_DEVICE_MARKERS } from './oldAddressMove'

export type Appearance = 'dark' | 'light'

export const APPEARANCE_KEY = 'kf49.appearance'
/** <meta name="theme-color"> in light: the canvas token (#f5f4f0), so the phone's status bar matches. */
export const LIGHT_THEME_COLOR = '#f5f4f0'

const listeners = new Set<() => void>()

/** Routes that only shared devices use (src/App.tsx): the clock face and the paired-device screens. */
const SHARED_DEVICE_PATHS = ['/clock-face', '/device-signin', '/device-capture'] as const

function isSharedDevicePath(pathname: string = typeof location === 'undefined' ? '' : location.pathname): boolean {
  return SHARED_DEVICE_PATHS.some(p => pathname === p || pathname.startsWith(p + '/'))
}

/** True when this browser is a shared device: on one of its routes, or it holds a kiosk / paired-device marker.
 *  Such a browser is always dark and offers no Appearance choice, so one worker cannot set it for everyone. */
export function isSharedDevice(pathname?: string): boolean {
  if (isSharedDevicePath(pathname)) return true
  try {
    return SHARED_DEVICE_MARKERS.some(k => {
      const v = localStorage.getItem(k)
      return !!v && v !== '0'
    })
  } catch {
    return false
  }
}

/** The saved choice. Dark when nothing is saved, the value is unknown, or storage throws. */
export function getAppearance(): Appearance {
  try {
    return localStorage.getItem(APPEARANCE_KEY) === 'light' ? 'light' : 'dark'
  } catch {
    return 'dark'
  }
}

/** Put the attribute on <html> to match `a`. Never on a shared device. */
export function applyAppearance(a: Appearance = snapshot()): void {
  if (typeof document === 'undefined') return
  const root = document.documentElement
  if (a === 'light' && !isSharedDevice()) root.setAttribute('data-theme', 'light')
  else root.removeAttribute('data-theme')
}

/** Save the choice and apply it live (no reload). A blocked storage still applies it for this tab. */
export function setAppearance(a: Appearance): void {
  memory = null
  try {
    if (a === 'light') localStorage.setItem(APPEARANCE_KEY, 'light')
    else localStorage.removeItem(APPEARANCE_KEY)
  } catch { memory = a /* storage blocked: the choice lasts until reload */ }
  applyAppearance(a)
  listeners.forEach(l => l())
}

// Set only when saving threw (private browsing, blocked storage): the in-tab choice.
let memory: Appearance | null = null

function snapshot(): Appearance {
  // A choice storage would not keep (setItem threw) wins, so the control and the page agree.
  return memory ?? stored() ?? 'dark'
}
/** What storage says: 'light' / 'dark', or null when it cannot be read. */
function stored(): Appearance | null {
  try { return localStorage.getItem(APPEARANCE_KEY) === 'light' ? 'light' : 'dark' } catch { return null }
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb)
  // Another tab changed it: follow, so two tabs agree.
  const onStorage = (e: StorageEvent) => {
    if (e.key === APPEARANCE_KEY || e.key === null) { applyAppearance(); cb() }
  }
  window.addEventListener('storage', onStorage)
  return () => { listeners.delete(cb); window.removeEventListener('storage', onStorage) }
}

/** The current appearance, re-rendering on a change here or in another tab. */
export function useAppearance(): [Appearance, (a: Appearance) => void] {
  const a = useSyncExternalStore(subscribe, snapshot, () => 'dark' as Appearance)
  return [a, setAppearance]
}
